import { useTranslation } from "react-i18next";

// Semi-circle gauge (∩), scale 200–600, opening downward.
// sweep-flag=1 + large-arc=0 draws the upper arc for any percent 0–1.
function GaugeChart({
  percent,
  completeLabel,
  approximatelyLabel,
}: {
  percent: number;
  completeLabel: string;
  approximatelyLabel: string;
}) {
  const cx = 110, cy = 100, r = 78, sw = 20;

  const pt = (deg: number) => ({
    x: cx + r * Math.cos((deg * Math.PI) / 180),
    y: cy - r * Math.sin((deg * Math.PI) / 180),
  });

  const left    = pt(180);
  const right   = pt(0);
  const fillDeg = 180 - percent * 180;
  const fillPt  = pt(fillDeg);

  const needleLen = r - 12;
  const needle = {
    x: cx + needleLen * Math.cos((fillDeg * Math.PI) / 180),
    y: cy - needleLen * Math.sin((fillDeg * Math.PI) / 180),
  };

  const scaleMarks = [
    { label: "200", deg: 180 },
    { label: "300", deg: 135 },
    { label: "400", deg: 90  },
    { label: "500", deg: 45  },
    { label: "600", deg: 0   },
  ];

  return (
    <svg width="100%" viewBox="0 0 220 150" style={{ maxWidth: 260 }}>
      {scaleMarks.map(({ label, deg }) => {
        const lp = {
          x: cx + (r + 20) * Math.cos((deg * Math.PI) / 180),
          y: cy - (r + 20) * Math.sin((deg * Math.PI) / 180),
        };
        return (
          <text
            key={label}
            x={lp.x}
            y={lp.y}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize={9}
            fill="#9ca3af"
          >
            {label}
          </text>
        );
      })}

      {/* Background track */}
      <path
        d={`M ${left.x} ${left.y} A ${r} ${r} 0 0 1 ${right.x} ${right.y}`}
        fill="none"
        stroke="#e5e7eb"
        strokeWidth={sw}
        strokeLinecap="round"
      />

      {/* Progress fill */}
      {percent > 0 && (
        <path
          d={`M ${left.x} ${left.y} A ${r} ${r} 0 0 1 ${fillPt.x} ${fillPt.y}`}
          fill="none"
          stroke="#9ca3af"
          strokeWidth={sw}
          strokeLinecap="round"
        />
      )}

      {/* Dashed needle */}
      <line
        x1={cx} y1={cy}
        x2={needle.x} y2={needle.y}
        stroke="#555"
        strokeWidth={1.5}
        strokeDasharray="3 4"
        opacity={0.6}
      />
      <circle cx={cx} cy={cy} r={4} fill="#9ca3af" />

      {/* Center label */}
      <text x={cx} y={cy + 16} textAnchor="middle" fontSize={24} fontWeight="700" fill="#6b7280">
        {Math.round(percent * 100)}%
      </text>
      <text x={cx} y={cy + 30} textAnchor="middle" fontSize={9} fill="#9ca3af">
        {completeLabel}
      </text>
      <text x={cx} y={cy + 43} textAnchor="middle" fontSize={8} fill="#c4c4c4" fontStyle="italic">
        {approximatelyLabel}
      </text>
    </svg>
  );
}

export function WeeklyGoal() {
  const { t } = useTranslation();

  return (
    <div className="bg-white rounded-2xl p-4 sm:p-5 border border-gray-100 shadow-sm flex flex-col gap-4">
      {/* Header */}
      <div>
        <h2 className="text-sm sm:text-base font-semibold text-gray-900">
          {t("affiliate.weeklyGoal")}
        </h2>
        <p className="text-xs text-gray-400 mt-0.5">
          {t("affiliate.weeklyGoalSubtitle")}
        </p>
      </div>

      {/* Gauge */}
      <div className="relative flex justify-center">
        <GaugeChart
          percent={0.64}
          completeLabel={t("affiliate.complete")}
          approximatelyLabel={t("affiliate.approximately")}
        />
        {/* Approximately stamp */}
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <span
            className="select-none text-xs font-bold tracking-[200%] uppercase px-3 py-1 rounded border-2 -rotate-[35deg]"
            style={{
              color: "#020637",
              borderColor: "rgba(2,6,55,0.35)",
            }}
          >
            {t("affiliate.approximately")}
          </span>
        </span>
      </div>

      {/* Completion Rate */}
      <div className="pt-3 border-t border-gray-100">
        <p className="text-xs font-semibold text-gray-400 mb-2">
          {t("affiliate.completionRate")}
        </p>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div className="h-full rounded-full bg-gray-400" style={{ width: "75%" }} />
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2">
          <span className="flex items-center gap-1.5 text-xs text-gray-400">
            <span className="w-2 h-2 rounded-full bg-gray-400 shrink-0" />
            {t("affiliate.avgRating", { value: "4.8" })}
          </span>
          <span className="flex items-center gap-1.5 text-xs text-gray-400">
            <span className="w-2 h-2 rounded-full bg-gray-300 shrink-0" />
            {t("affiliate.reviews", { count: 156 })}
          </span>
        </div>
      </div>
    </div>
  );
}
